import React from 'react';
import {
  Box,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from '@mui/material';
import { Search, PersonAdd } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface UserFiltersProps {
  filters: {
    role: 'buyer' | 'admin' | 'seller' | '';
    status: 'active' | 'suspended' | 'deactivated' | '';
    search: string;
  };
  setFilters: (filters: any) => void;
  selectedCount: number;
  onCreateSeller: () => void;
  onBulkSuspend: () => void;
}

const UserFilters: React.FC<UserFiltersProps> = ({
  filters,
  setFilters,
  selectedCount,
  onCreateSeller,
  onBulkSuspend,
}) => {
  const { t } = useTranslation();

  return (
    <Paper
      sx={{
        p: 2,
        mb: 3,
        bgcolor: 'background.paper',
        borderRadius: '8px',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexWrap: 'wrap',
          flexDirection: { xs: 'column', sm: 'row' },
        }}
      >
        <TextField
          size="small"
          placeholder={t('admin.searchUsers')}
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          InputProps={{
            startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
          }}
          sx={{ minWidth: { xs: '100%', sm: 300 } }}
        />

        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 120 } }}>
          <InputLabel>{t('auth.role')}</InputLabel>
          <Select
            value={filters.role}
            onChange={(e) => setFilters({ ...filters, role: e.target.value as any })}
            label={t('auth.role')}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            <MenuItem value="buyer">{t('auth.buyer')}</MenuItem>
            <MenuItem value="admin">{t('auth.admin')}</MenuItem>
            <MenuItem value="seller">{t('auth.seller', { defaultValue: 'Seller' })}</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 120 } }}>
          <InputLabel>{t('common.status')}</InputLabel>
          <Select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value as any })}
            label={t('common.status')}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            <MenuItem value="active">{t('warranty.active')}</MenuItem>
            <MenuItem value="suspended">{t('warranty.pending')}</MenuItem>
            <MenuItem value="deactivated">{t('admin.deactivated')}</MenuItem>
          </Select>
        </FormControl>

        <Box sx={{ flexGrow: 1 }} />

        <Button
          variant="contained"
          color="primary"
          startIcon={<PersonAdd />}
          onClick={onCreateSeller}
          size="small"
          sx={{
            minWidth: { xs: '100%', sm: 'auto' },
            fontSize: { xs: '0.875rem', sm: '1rem' },
          }}
        >
          {t('admin.createSeller', { defaultValue: 'Create Seller' })}
        </Button>

        {selectedCount > 0 && (
          <Button
            variant="outlined"
            color="warning"
            onClick={onBulkSuspend}
            size="small"
            sx={{
              minWidth: { xs: '100%', sm: 'auto' },
              fontSize: { xs: '0.875rem', sm: '1rem' },
            }}
          >
            {t('admin.bulkSuspend')} ({selectedCount})
          </Button>
        )}
      </Box>
    </Paper>
  );
};

export default UserFilters;